import React, { useState } from "react";
import { useGetProductsQuery, useGetStockItemsQuery, useCreateProductMutation, useUpdateProductMutation, useDeleteProductMutation } from "../services/api";
import { FiEdit2, FiTrash2, FiPlus, FiPackage } from "react-icons/fi";

function formatCurrency(value) {
  return new Intl.NumberFormat("en-ET", { style: "currency", currency: "ETB" }).format(value || 0);
}

export default function ProductsPage() {
  const { data, isLoading } = useGetProductsQuery({ active: "all" });
  const { data: stockData } = useGetStockItemsQuery();
  const [createProduct] = useCreateProductMutation();
  const [updateProduct] = useUpdateProductMutation();
  const [deleteProduct] = useDeleteProductMutation();

  const products = data?.data || [];
  const stockItems = stockData?.data || [];

  // Calculate KPIs
  const totalProducts = products.length;
  const activeProducts = products.filter(p => p.is_active).length;
  const totalValue = products.reduce((sum, p) => sum + (Number(p.selling_price) * 1), 0);
  const categories = [...new Set(products.map(p => p.category))].length;

  const [showModal, setShowModal] = useState(false);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    name: "",
    sku: "",
    category: "gold",
    material_type: "gold",
    stock_item_id: "",
    weight_grams: "",
    unit: "pc",
    cost_price: "",
    selling_price: "",
    description: "",
    is_active: true,
  });
  const [quickAddForm, setQuickAddForm] = useState({
    name: "",
    selling_price: "",
  });
  const [error, setError] = useState("");

  const openModal = (product = null) => {
    if (product) {
      setEditingId(product.id);
      setForm({
        name: product.name,
        sku: product.sku || "",
        category: product.category,
        material_type: product.material_type,
        stock_item_id: product.stock_item_id || "",
        weight_grams: product.weight_grams,
        unit: product.unit || "pc",
        cost_price: product.cost_price,
        selling_price: product.selling_price,
        description: product.description || "",
        is_active: product.is_active,
      });
    } else {
      setEditingId(null);
      setForm({
        name: "",
        sku: "",
        category: "gold",
        material_type: "gold",
        stock_item_id: "",
        weight_grams: "",
        unit: "pc",
        cost_price: "",
        selling_price: "",
        description: "",
        is_active: true,
      });
    }
    setError("");
    setShowModal(true);
  };

  const openQuickAddModal = () => {
    setQuickAddForm({
      name: "",
      selling_price: "",
    });
    setError("");
    setShowQuickAddModal(true);
  };

  // Smart detection from product name
  const detectProductDetails = (name) => {
    const nameLower = name.toLowerCase();
    
    // Detect category and material type
    let category = "gold"; // default
    let material_type = "gold"; // default
    
    if (nameLower.includes("silver")) {
      category = "silver";
      material_type = "silver";
    } else if (nameLower.includes("gold")) {
      category = "gold";
      material_type = "gold";
    } else if (nameLower.includes("gemstone") || nameLower.includes("diamond") || nameLower.includes("ruby")) {
      category = "gemstone";
      material_type = "gemstone";
    }
    
    // Detect weight based on product type
    let weight_grams = 50; // default
    if (nameLower.includes("ring")) weight_grams = 50;
    else if (nameLower.includes("necklace")) weight_grams = 100;
    else if (nameLower.includes("bracelet")) weight_grams = 75;
    else if (nameLower.includes("earring")) weight_grams = 30;
    else if (nameLower.includes("pendant")) weight_grams = 40;
    else if (nameLower.includes("chain")) weight_grams = 80;
    
    return { category, material_type, weight_grams };
  };

  // Auto-generate SKU from name
  const generateSKU = (name) => {
    const words = name.trim().split(/\s+/);
    const prefix = words.map(w => w[0].toUpperCase()).join('').substring(0, 3);
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `${prefix}-${random}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editingId) {
        await updateProduct({ id: editingId, ...form }).unwrap();
      } else {
        await createProduct(form).unwrap();
      }
      setShowModal(false);
    } catch (err) {
      setError(err?.data?.message || "Failed to save product");
    }
  };

  const handleQuickAddSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!quickAddForm.name || !quickAddForm.selling_price) {
      setError("Please enter product name and selling price");
      return;
    }

    try {
      // Detect product details from name
      const detected = detectProductDetails(quickAddForm.name);
      
      // Auto-calculate cost price (60% of selling price)
      const selling_price = Number(quickAddForm.selling_price);
      const cost_price = Math.round(selling_price * 0.6);
      
      // Create product with smart defaults
      const productPayload = {
        name: quickAddForm.name,
        sku: generateSKU(quickAddForm.name),
        category: detected.category,
        material_type: detected.material_type,
        weight_grams: detected.weight_grams,
        cost_price: cost_price,
        selling_price: selling_price,
        description: "Quick add",
        is_active: true,
      };
      
      await createProduct(productPayload).unwrap();
      setShowQuickAddModal(false);
    } catch (err) {
      setError(err?.data?.message || "Failed to add product");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await deleteProduct(id).unwrap();
      } catch (err) {
        alert(err?.data?.message || "Failed to delete product");
      }
    }
  };

  if (isLoading) {
    return (
      <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
        <div style={{ marginBottom: "2rem" }}>
          <div style={{ 
            height: "32px", 
            width: "180px", 
            background: "#E2E8F0", 
            borderRadius: "6px",
            marginBottom: "0.75rem",
            animation: "pulse 1.5s ease-in-out infinite"
          }}></div>
        </div>
        <div style={{ 
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: "1.25rem"
        }}>
          {[1,2,3,4].map(i => (
            <div key={i} style={{
              background: "white",
              border: "1px solid #E2E8F0",
              borderRadius: "12px",
              padding: "1.5rem",
              animation: "pulse 1.5s ease-in-out infinite"
            }}>
              <div style={{ height: "16px", width: "60%", background: "#E2E8F0", borderRadius: "4px", marginBottom: "1rem" }}></div>
              <div style={{ height: "32px", width: "40%", background: "#E2E8F0", borderRadius: "4px" }}></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "#F8F9FA", minHeight: "100vh", padding: "2rem" }}>
      {/* Page Header */}
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center", 
        marginBottom: "2rem" 
      }}>
        <div>
          <h1 style={{ 
            fontSize: "2rem", 
            marginBottom: "0.5rem", 
            fontWeight: 600,
            color: "#1E293B"
          }}>
            Products
          </h1>
          <p style={{ color: "#64748B", fontSize: "0.95rem" }}>
            Manage your jewellery product catalogue
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button 
            onClick={() => openQuickAddModal()} 
            style={{
              padding: "0.75rem 1.5rem",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "0.95rem",
              fontWeight: 600,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-1px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.4)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(59, 130, 246, 0.3)";
            }}
          >
            <FiPlus /> ⚡ Quick Add
          </button>
          <button 
            onClick={() => openModal()}
            style={{
              padding: "0.75rem 1.5rem",
              background: "white",
              color: "#3B82F6",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              fontSize: "0.95rem",
              fontWeight: 500,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#3B82F6";
              e.currentTarget.style.background = "#F8FAFC";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#E2E8F0";
              e.currentTarget.style.background = "white";
            }}
          >
            <FiPlus /> Advanced
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ 
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: "1.25rem",
        marginBottom: "2rem"
      }}>
        {/* Total Products */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              📦
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Total Products
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {totalProducts}
          </div>
        </div>

        {/* Active Products */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              ✓
            </div>
            <div style={{
              fontSize: "0.8rem",
              color: "#64748B",
              fontWeight: 600
            }}>
              {activeProducts} active
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Active Products
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {activeProducts}
          </div>
        </div>

        {/* Total Value */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              💰
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Catalog Value
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {formatCurrency(totalValue)}
          </div>
        </div>

        {/* Categories */}
        <div style={{ 
          background: "white",
          border: "1px solid #E2E8F0",
          borderRadius: "12px",
          padding: "1.5rem",
          transition: "all 0.3s",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.08)";
          e.currentTarget.style.transform = "translateY(-2px)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "none";
          e.currentTarget.style.transform = "translateY(0)";
        }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <div style={{ 
              width: "48px",
              height: "48px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: "1.25rem"
            }}>
              🏷️
            </div>
          </div>
          <div style={{ fontSize: "0.85rem", color: "#64748B", marginBottom: "0.5rem", fontWeight: 500 }}>
            Categories
          </div>
          <div style={{ fontSize: "1.75rem", fontWeight: 700, color: "#1E293B" }}>
            {categories}
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div style={{
        background: "white",
        border: "1px solid #E2E8F0",
        borderRadius: "12px",
        padding: "1.5rem",
        overflowX: "auto"
      }}>
        <table style={{ 
          width: "100%", 
          borderCollapse: "collapse",
          fontSize: "0.9rem"
        }}>
          <thead>
            <tr style={{ borderBottom: "2px solid #E2E8F0" }}>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Product</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Category</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Material</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Weight</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Unit</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Cost</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Price</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Profit</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Status</th>
              <th style={{ textAlign: "left", padding: "0.75rem", fontWeight: 600, color: "#64748B", fontSize: "0.85rem" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr>
                <td colSpan="10" style={{ textAlign: "center", padding: "3rem" }}>
                  <FiPackage size={48} style={{ color: "#CBD5E1", marginBottom: "0.75rem" }} />
                  <div style={{ color: "#64748B" }}>No products found</div>
                </td>
              </tr>
            ) : (
              products.map((product) => {
                const profit = Number(product.selling_price) - Number(product.cost_price);
                const profitMargin = product.cost_price > 0 ? (profit / product.cost_price * 100).toFixed(1) : 0;
                return (
                  <tr key={product.id} style={{ 
                    borderBottom: "1px solid #F1F5F9",
                    transition: "all 0.2s"
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = "#F8FAFC";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "white";
                  }}
                  >
                    <td style={{ padding: "1rem 0.75rem" }}>
                      <div style={{ fontWeight: 600, color: "#1E293B", marginBottom: "0.25rem" }}>{product.name}</div>
                      {product.sku && <div style={{ fontSize: "0.8rem", color: "#64748B" }}>{product.sku}</div>}
                    </td>
                    <td style={{ padding: "1rem 0.75rem" }}>
                      <span style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        background: "#FEF3C7",
                        color: "#92400E"
                      }}>
                        {product.category}
                      </span>
                    </td>
                    <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{product.material_type}</td>
                    <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{Number(product.weight_grams).toFixed(3)}g</td>
                    <td style={{ padding: "1rem 0.75rem" }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        background: product.unit === 'grams' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                        color: product.unit === 'grams' ? '#F59E0B' : '#3B82F6'
                      }}>
                        {product.unit === 'grams' ? '⚖️ grams' : '📦 pc'}
                      </span>
                    </td>
                    <td style={{ padding: "1rem 0.75rem", color: "#64748B" }}>{formatCurrency(product.cost_price)}</td>
                    <td style={{ padding: "1rem 0.75rem", fontWeight: 600, color: "#1E293B" }}>{formatCurrency(product.selling_price)}</td>
                    <td style={{ padding: "1rem 0.75rem" }}>
                      <div style={{ color: profit >= 0 ? "#10B981" : "#EF4444", fontWeight: 600 }}>
                        {formatCurrency(profit)}
                      </div>
                      <div style={{ fontSize: "0.75rem", color: "#9CA3AF" }}>
                        {profitMargin}% margin
                      </div>
                    </td>
                    <td style={{ padding: "1rem 0.75rem" }}>
                      <span style={{
                        padding: "4px 10px",
                        borderRadius: "6px",
                        fontSize: "0.8rem",
                        fontWeight: 600,
                        background: product.is_active ? "#D1FAE5" : "#F1F5F9",
                        color: product.is_active ? "#065F46" : "#64748B"
                      }}>
                        {product.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td style={{ padding: "1rem 0.75rem" }}>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button 
                          onClick={() => openModal(product)}
                          style={{
                            padding: "0.5rem",
                            background: "transparent",
                            border: "1px solid #E2E8F0",
                            borderRadius: "6px",
                            color: "#3B82F6",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            transition: "all 0.2s"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#F8FAFC";
                            e.currentTarget.style.borderColor = "#3B82F6";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.borderColor = "#E2E8F0";
                          }}
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button 
                          onClick={() => handleDelete(product.id)}
                          style={{
                            padding: "0.5rem",
                            background: "transparent",
                            border: "1px solid #E2E8F0",
                            borderRadius: "6px",
                            color: "#EF4444",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            transition: "all 0.2s"
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#FEF2F2";
                            e.currentTarget.style.borderColor = "#EF4444";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                            e.currentTarget.style.borderColor = "#E2E8F0";
                          }}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editingId ? "Edit Product" : "Add Product"}</h3>
              <button className="btn-ghost" onClick={() => setShowModal(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Product Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g., Silver Ring, Gold Necklace, Diamond Bracelet"
                    required
                  />
                  <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                    Enter the full product name (e.g., "24K Gold Wedding Ring")
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">SKU (Product Code)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={form.sku}
                      onChange={(e) => setForm({ ...form, sku: e.target.value })}
                      placeholder="e.g., GR-001, SR-123"
                    />
                    <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                      Optional: Unique product code for tracking
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Category *</label>
                    <select className="form-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                      <option value="gold">Gold Jewellery</option>
                      <option value="silver">Silver Jewellery</option>
                      <option value="gemstone">Gemstone Jewellery</option>
                      <option value="custom">Custom/Mixed</option>
                    </select>
                    <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                      Main category for this product
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">Material Type *</label>
                    <select className="form-select" value={form.material_type} onChange={(e) => setForm({ ...form, material_type: e.target.value })}>
                      <option value="gold">Gold</option>
                      <option value="silver">Silver</option>
                      <option value="gemstone">Gemstone</option>
                      <option value="mixed">Mixed Materials</option>
                    </select>
                    <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                      Primary material used (affects stock tracking)
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Stock Item (Optional)</label>
                    <select 
                      className="form-select" 
                      value={form.stock_item_id} 
                      onChange={(e) => setForm({ ...form, stock_item_id: e.target.value })}
                    >
                      <option value="">-- Auto-select by material type --</option>
                      {stockItems
                        .filter(item => item.material_type === form.material_type)
                        .map(item => (
                          <option key={item.id} value={item.id}>
                            {item.name} ({Number(item.computed_quantity || item.quantity || 0).toFixed(2)} {item.unit}) - {item.origin === 'local' ? '🏠 Local' : '🌍 Foreign'}
                          </option>
                        ))}
                    </select>
                    <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                      Link to specific stock item or leave blank for auto-selection
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">Weight (grams) *</label>
                    <input
                      type="number"
                      className="form-input"
                      value={form.weight_grams}
                      onChange={(e) => setForm({ ...form, weight_grams: e.target.value })}
                      placeholder="e.g., 50.5"
                      step="0.001"
                      min="0"
                      required
                    />
                    <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                      {form.unit === 'grams' ? 'Available grams for sale (e.g., 4000g)' : 'Weight per piece (e.g., 50.5g)'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Selling Unit *</label>
                    <select
                      className="form-select"
                      value={form.unit}
                      onChange={(e) => setForm({ ...form, unit: e.target.value })}
                      required
                    >
                      <option value="pc">📦 Pieces (pc) - For unique items like rings, necklaces</option>
                      <option value="grams">⚖️ Grams - For bulk gold/silver sold by weight</option>
                    </select>
                    <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                      {form.unit === 'pc' ? 'Sold as individual pieces (1 ring, 1 necklace, etc.)' : 'Sold by weight (customer chooses grams in POS)'}
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-4)" }}>
                  <div className="form-group">
                    <label className="form-label">Cost Price (ETB) *</label>
                    <input
                      type="number"
                      className="form-input"
                      value={form.cost_price}
                      onChange={(e) => setForm({ ...form, cost_price: e.target.value })}
                      placeholder="e.g., 5000"
                      step="0.01"
                      min="0"
                      required
                    />
                    <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                      How much it costs you (materials + labor)
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Selling Price (ETB) *</label>
                    <input
                      type="number"
                      className="form-input"
                      value={form.selling_price}
                      onChange={(e) => setForm({ ...form, selling_price: e.target.value })}
                      placeholder="e.g., 8000"
                      step="0.01"
                      min="0"
                      required
                    />
                    <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                      Price you sell to customers
                    </div>
                  </div>
                </div>

                {/* Profit Preview */}
                {form.cost_price && form.selling_price && (
                  <div style={{
                    padding: "var(--space-3)",
                    background: "var(--color-surface-elevated)",
                    borderRadius: "var(--border-radius-sm)",
                    marginBottom: "var(--space-4)",
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "var(--space-1)" }}>
                      <span style={{ color: "var(--color-text-secondary)" }}>Profit per item:</span>
                      <span style={{ fontWeight: 600, color: "var(--color-success)" }}>
                        {formatCurrency(Number(form.selling_price) - Number(form.cost_price))}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "var(--color-text-secondary)" }}>Profit margin:</span>
                      <span style={{ fontWeight: 600, color: "var(--color-accent)" }}>
                        {((Number(form.selling_price) - Number(form.cost_price)) / Number(form.cost_price) * 100).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Description (Optional)</label>
                  <textarea
                    className="form-textarea"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="e.g., Beautiful 24K gold ring with intricate design, perfect for weddings"
                    rows="3"
                  />
                  <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px" }}>
                    Additional details about the product (design, features, etc.)
                  </div>
                </div>

                <div className="form-group">
                  <label style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    />
                    <span className="form-label" style={{ marginBottom: 0 }}>Active (Available for sale)</span>
                  </label>
                  <div style={{ fontSize: "0.8125rem", color: "var(--color-text-secondary)", marginTop: "4px", marginLeft: "28px" }}>
                    Uncheck to hide this product from POS and sales
                  </div>
                </div>

                {error && <div className="form-error">{error}</div>}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingId ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Add Product Modal */}
      {showQuickAddModal && (
        <div className="modal-overlay" onClick={() => setShowQuickAddModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "500px" }}>
            <div className="modal-header">
              <h3 className="modal-title">⚡ Quick Add Product</h3>
              <button className="btn-ghost" onClick={() => setShowQuickAddModal(false)}>×</button>
            </div>
            <form onSubmit={handleQuickAddSubmit}>
              <div className="modal-body">
                <div style={{
                  padding: "var(--space-4)",
                  background: "var(--color-surface-elevated)",
                  borderRadius: "var(--border-radius-sm)",
                  marginBottom: "var(--space-4)",
                  fontSize: "0.875rem",
                  color: "var(--color-text-secondary)",
                }}>
                  <div style={{ fontWeight: 600, marginBottom: "var(--space-2)", color: "var(--color-text)" }}>
                    💡 Smart Detection
                  </div>
                  <div>• Name with "ring" → 50g weight</div>
                  <div>• Name with "necklace" → 100g weight</div>
                  <div>• Name with "bracelet" → 75g weight</div>
                  <div>• Name with "silver" → Silver category</div>
                  <div>• Name with "gold" → Gold category</div>
                  <div style={{ marginTop: "var(--space-2)", fontStyle: "italic" }}>
                    Example: "Silver Ring" → Silver, 50g, Auto SKU
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontSize: "1rem", fontWeight: 600 }}>
                    Product Name *
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={quickAddForm.name}
                    onChange={(e) => setQuickAddForm({ ...quickAddForm, name: e.target.value })}
                    placeholder="e.g., Silver Ring, Gold Necklace, etc."
                    required
                    autoFocus
                    style={{ fontSize: "1.125rem", padding: "12px" }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" style={{ fontSize: "1rem", fontWeight: 600 }}>
                    Selling Price (ETB) *
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={quickAddForm.selling_price}
                    onChange={(e) => setQuickAddForm({ ...quickAddForm, selling_price: e.target.value })}
                    placeholder="e.g., 8000"
                    step="0.01"
                    min="0"
                    required
                    style={{ fontSize: "1.125rem", padding: "12px" }}
                  />
                </div>

                <div style={{
                  padding: "var(--space-3)",
                  background: "rgba(255, 215, 0, 0.1)",
                  border: "1px solid var(--color-accent)",
                  borderRadius: "var(--border-radius-sm)",
                  fontSize: "0.875rem",
                }}>
                  <div style={{ fontWeight: 600, marginBottom: "var(--space-1)" }}>Auto-filled defaults:</div>
                  <div>• SKU: Auto-generated from name</div>
                  <div>• Category: Detected from name</div>
                  <div>• Weight: Based on product type</div>
                  <div>• Cost Price: 60% of selling price</div>
                  <div style={{ marginTop: "var(--space-2)", fontSize: "0.8125rem", fontStyle: "italic" }}>
                    You can edit these later if needed
                  </div>
                </div>

                {error && <div className="form-error">{error}</div>}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowQuickAddModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ fontSize: "1.125rem", padding: "12px 24px" }}>
                  ⚡ Quick Add
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
